import { Fragment, useLayoutEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Box, Skeleton, useTheme } from '@mui/material';
import {
  IconSelector,
  IIconSelectorValue,
  ILayout,
  usePerformanceReportBuilderPageStore,
} from '@diana/core';
import { useUpdateLayoutIconMutation } from '../Mutations/useUpdateLayoutIconMutation';
import { usePerformanceReportBuilderPageContext } from '../PerformanceReportBuilderPageContext';

export const LayoutIconSelector = () => {
  const { palette } = useTheme();

  const { isLoading, error } = usePerformanceReportBuilderPageContext();

  const layout = usePerformanceReportBuilderPageStore((s) => s.layout);

  const [iconData, setIconData] = useState<ILayout['iconData']>(layout?.iconData);

  const { mutate } = useUpdateLayoutIconMutation();

  const updateIconDebounce = useDebouncedCallback(mutate, 1500);

  const value = useMemo<IIconSelectorValue>(() => {
    if (iconData) {
      return {
        color: iconData.color,
        icon: iconData.icon,
        imageUrl: iconData.imageUrl,
      };
    }

    return {
      color: palette.background.paper,
      icon: 'AddOutlined',
      iconColor: palette.text.secondary,
    };
  }, [iconData, palette]);

  useLayoutEffect(() => {
    setIconData(layout?.iconData);
  }, [layout?.iconData]);

  if (isLoading)
    return (
      <Box>
        <Skeleton variant="rounded" width={30} height={30} />
      </Box>
    );

  if (!error)
    return (
      <Box
        data-testid="LayoutIconSelectorContainer"
        sx={({ palette, spacing }) => ({
          border: error ? `1px solid ${palette.error.main}` : undefined,
          borderRadius: spacing(0.5),
        })}
      >
        <IconSelector
          size={32}
          value={value}
          onChange={(v) => {
            setIconData(v);
            updateIconDebounce(v);
          }}
        />
      </Box>
    );

  return <Fragment />;
};
